import { useContext, useEffect, useState, type ReactNode } from 'react';
import { Button, Field, HStack, Input, NativeSelect, Spinner, Stack, Text } from '@chakra-ui/react';
import { redactCredentials, type Bridge } from '@casa/bridge';
import { startDriverProbe, type DriverCatalog, type ResolvedDriver } from '@casa/driver-host';
import type { DriverManifest, TestResult } from '@casa/driver-sdk';
import { BUILT_IN_MANIFESTS } from '@casa/drivers/manifests';
import type { Connection, Dashboard } from '@casa/model';
import { LiveDataContext, type ConnectionStatus } from '../data/live_data.ts';
import { discoverSystems, type FoundSystem } from '../drivers/discovery.ts';
import { DriverInstall } from '../drivers/driver_install.tsx';
import { createDriverWorker, loadDrivers } from '../drivers/drivers.ts';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';
import { ChromeDialog } from '../ui/chrome_dialog.tsx';
import { ChromeError, ChromeNote } from '../ui/chrome_page.tsx';
import { configFields, configFrom, configProblems, connectionAddress, shownValue, type ConfigField } from './config_form.ts';
import { tilesUsing } from './dashboard_edits.ts';

// The dashboard's connections, in edit mode (dashboard-editor spec §7; the owner chose edit mode,
// 2026-10-16): the list with each connection's driver, address, status and tiles (C-1); adding one
// by its driver, with the systems found on the network first (C-2, M-8); its configuration as a
// form, secrets replace-only (C-3); a test before keeping it (C-4); deleting, saying which tiles use
// it (C-5). Changes go into the working copy, kept with Save like every other edit.

/** A connection's name, as the dashboard's schema takes it. */
const NAME_PATTERN = /^[A-Za-z0-9_-]+$/;

export interface ConnectionsDialogProps {
    shell: Bridge;
    dashboard: Dashboard;
    /** Keeps a connection, under `name`, replacing `previous` (a rename) when given. */
    onKeep: (name: string, connection: Connection, previous: string | undefined) => void;
    onDelete: (name: string) => void;
    onClose: () => void;
    /**
     * Opens on adding a connection and closes once it is kept or left: for the tile dialog, where a
     * tile's data is chosen from a connection that is not there yet (C-6).
     */
    startsAdding?: boolean;
}

type View = { kind: 'list' } | { kind: 'drivers' } | { kind: 'install' } | { kind: 'form'; driver: string; name: string | undefined };

const STATUS_WORDS: Record<ConnectionStatus['status'], string> = { ok: 'working', failing: 'failing', waiting: 'waiting for an answer', unused: 'no tiles yet' };

const messageOf = (error: unknown) : string => (error instanceof Error ? error.message : String(error));

/** Text shown to the user, with credentials masked: those the bridge recognises, and this connection's secrets. */
function masked(text: string, secrets: readonly unknown[]) : string {
    let result = redactCredentials(text);
    for (const secret of secrets) {
        if (typeof secret === 'string' && secret.length >= 3) {
            result = result.split(secret).join('***');
        }
    }

    return result;
}

export function ConnectionsDialog(props: ConnectionsDialogProps) : ReactNode {
    const { shell, dashboard, onKeep, onDelete, onClose, startsAdding = false } = props;
    const [view, setView] = useState<View>({ kind: startsAdding ? 'drivers' : 'list' });
    const [catalog, setCatalog] = useState<DriverCatalog>();
    const live = useContext(LiveDataContext);
    const [, setStatusVersion] = useState(0);

    useEffect(() => {
        void loadDrivers(shell).then(setCatalog);
    }, [shell]);

    // The list shows each connection's status as it changes, not as it was when the dialog opened.
    const connectionNames = Object.keys(dashboard.connections).join('\u0000');
    useEffect(() => {
        if (!live) {
            return undefined;
        }

        const unsubscribes = connectionNames.split('\u0000').map((name) => live.subscribeConnection(name, () => setStatusVersion((version) => version + 1)));

        return () => {
            for (const unsubscribe of unsubscribes) {
                unsubscribe();
            }
        };
    }, [live, connectionNames]);

    if (view.kind === 'install' && catalog) {
        return (
            <ChromeDialog title="Add a driver" onClose={onClose} focusesField={false} fillsPhone footer={<Button variant="outline" onClick={() => setView({ kind: 'drivers' })}>Back</Button>}>
                <DriverInstall
                    shell={shell}
                    installed={catalog.installed}
                    onInstalled={(manifest) => {
                        // The new driver's connection follows at once (C-7 fits into C-2).
                        void loadDrivers(shell).then((loaded) => {
                            setCatalog(loaded);
                            setView({ kind: 'form', driver: manifest.id, name: undefined });
                        });
                    }}
                />
            </ChromeDialog>
        );
    }

    const resolve = (driver: string) : ResolvedDriver => catalog?.resolve(driver) ?? { ok: false, reason: 'loading the drivers' };

    if (view.kind === 'form') {
        const resolved = resolve(view.driver);
        const existing = view.name === undefined ? undefined : dashboard.connections[view.name];

        return (
            <ConnectionForm
                shell={shell}
                dashboard={dashboard}
                driver={view.driver}
                resolved={resolved}
                name={view.name}
                stored={existing?.config ?? {}}
                onKeep={(name, config) => {
                    onKeep(name, { driver: view.driver, config }, view.name);
                    if (startsAdding) {
                        onClose();
                    } else {
                        setView({ kind: 'list' });
                    }
                }}
                onDelete={view.name === undefined ? undefined : () => {
                    onDelete(view.name!);
                    setView({ kind: 'list' });
                }}
                onBack={() => setView(view.name === undefined ? { kind: 'drivers' } : { kind: 'list' })}
            />
        );
    }

    if (view.kind === 'drivers') {
        const builtIn = Object.values(BUILT_IN_MANIFESTS);

        return (
            <ChromeDialog title="Add a connection" onClose={onClose} focusesField={false} fillsPhone footer={<Button variant="outline" onClick={() => (startsAdding ? onClose() : setView({ kind: 'list' }))}>Back</Button>}>
                {!catalog ? (
                    <HStack gap="2" color="fg.muted">
                        <Spinner size="sm" />
                        <Text>Loading the drivers…</Text>
                    </HStack>
                ) : (
                    <SettingsGroup title="Its driver" note={catalog.profile.userDrivers ? 'Drivers added here are installed on this device; Settings, App settings, Drivers lists them.' : undefined}>
                        {builtIn.map((manifest) => (
                            <SettingsRow key={manifest.id} icon="puzzle-piece" title={manifest.name} detail={manifest.description} onClick={() => setView({ kind: 'form', driver: manifest.id, name: undefined })} />
                        ))}
                        {catalog.installed.map((driver) =>
                            driver.ok ? (
                                <SettingsRow key={driver.record.id} icon="puzzle-piece" title={`${driver.manifest.name} ${driver.record.version}`} detail={driver.manifest.description ?? 'installed'} onClick={() => setView({ kind: 'form', driver: driver.record.id, name: undefined })} />
                            ) : null,
                        )}
                        {catalog.profile.userDrivers ? (
                            <SettingsRow icon="plus" title="Add a driver…" detail="From a folder, a zip file or a web address" color="#60a5fa" opens={false} onClick={() => setView({ kind: 'install' })} />
                        ) : null}
                    </SettingsGroup>
                )}
            </ChromeDialog>
        );
    }

    const names = Object.keys(dashboard.connections);

    return (
        <ChromeDialog title="Connections" onClose={onClose} focusesField={false} fillsPhone footer={<Button colorPalette="blue" onClick={onClose}>Done</Button>}>
            <SettingsGroup note="The systems this dashboard reads from. Changes are kept with Save, like the other edits.">
                {names.map((name) => {
                    const connection = dashboard.connections[name]!;
                    const resolved = resolve(connection.driver);
                    const address = resolved.ok ? connectionAddress(resolved.manifest.permissions.configHosts, connection.config) : undefined;
                    const status = live?.connectionStatus(name);
                    const tiles = tilesUsing(dashboard, name).length;
                    const detail = [
                        resolved.ok ? resolved.manifest.name : connection.driver,
                        address,
                        status ? (status.status === 'failing' ? `failing: ${masked(status.reason, Object.values(connection.config))}` : STATUS_WORDS[status.status]) : 'not running yet',
                        tiles === 0 ? undefined : `${tiles} tile${tiles === 1 ? '' : 's'}`,
                    ].filter((part) => part !== undefined).join(' · ');

                    return (
                        <SettingsRow
                            key={name}
                            icon="plug"
                            title={name}
                            detail={detail}
                            color={status?.status === 'failing' ? '#f87171' : undefined}
                            onClick={() => setView({ kind: 'form', driver: connection.driver, name })}
                        />
                    );
                })}
                <SettingsRow icon="plus" title="Add a connection" color="#60a5fa" opens={false} onClick={() => setView({ kind: 'drivers' })} />
            </SettingsGroup>
        </ChromeDialog>
    );
}

interface ConnectionFormProps {
    shell: Bridge;
    dashboard: Dashboard;
    driver: string;
    resolved: ResolvedDriver;
    /** The connection's name; undefined for a new one. */
    name: string | undefined;
    stored: Readonly<Record<string, unknown>>;
    onKeep: (name: string, config: Record<string, unknown>) => void;
    onDelete: (() => void) | undefined;
    onBack: () => void;
}

type Tested = { status: 'testing' } | { status: 'done'; result: TestResult } | { status: 'failed'; reason: string };

function ConnectionForm(props: ConnectionFormProps) : ReactNode {
    const { shell, dashboard, driver, resolved, name, stored, onKeep, onDelete, onBack } = props;
    const fields: ConfigField[] = resolved.ok ? configFields(resolved.manifest.configSchema) : [];
    const [typedName, setTypedName] = useState(name ?? suggestedName(dashboard, driver));
    const [typed, setTyped] = useState<Record<string, string>>(() => Object.fromEntries(fields.map((field) => [field.name, shownValue(field, stored[field.name])])));
    const [tested, setTested] = useState<Tested>();
    const [isAskingDelete, setAskingDelete] = useState(false);

    const config = configFrom(fields, typed, stored);
    const secrets = fields.filter((field) => field.kind === 'secret').map((field) => config[field.name]);
    const trimmedName = typedName.trim();
    const nameProblem = !NAME_PATTERN.test(trimmedName)
        ? 'A name of letters, digits, "-" and "_"'
        : trimmedName !== name && dashboard.connections[trimmedName] !== undefined
          ? `There is a connection called "${trimmedName}" already`
          : undefined;
    const problems = configProblems(driver, fields, config);
    const using = name === undefined ? [] : tilesUsing(dashboard, name);

    const test = async () : Promise<void> => {
        if (!resolved.ok) {
            return;
        }

        setTested({ status: 'testing' });
        const probe = startDriverProbe({
            bridge: shell,
            createWorker: createDriverWorker,
            connection: `test-${trimmedName || driver}`,
            driver: resolved.code,
            permissions: resolved.permissions,
            config,
            log: (message) => console.warn(masked(message, secrets)),
        });
        try {
            const result = await probe.ask({ kind: 'test' });
            // What the test obtained (a key the bridge gave when paired): into the form, to keep.
            if (result.ok && result.config) {
                const obtained = Object.entries(result.config).filter(([field]) => fields.some((each) => each.name === field));
                setTyped((before) => ({ ...before, ...Object.fromEntries(obtained) }));
            }
            setTested({ status: 'done', result });
        } catch (error) {
            setTested({ status: 'failed', reason: messageOf(error) });
        } finally {
            probe.dispose();
        }
    };

    if (isAskingDelete) {
        return (
            <ChromeDialog
                title={`Delete "${name}"?`}
                onClose={() => setAskingDelete(false)}
                isQuestion
                footer={
                    <>
                        <Button variant="outline" onClick={() => setAskingDelete(false)}>
                            Keep it
                        </Button>
                        <Button colorPalette="red" onClick={onDelete}>
                            Delete
                        </Button>
                    </>
                }
            >
                <Stack gap="2">
                    <Text>These tiles read from it, and will show no data until they get another connection:</Text>
                    {using.map(({ screen, tile }) => (
                        <Text key={tile.id} color="fg.muted">
                            {screen}: {tile.label || tile.type}
                        </Text>
                    ))}
                </Stack>
            </ChromeDialog>
        );
    }

    const title = name === undefined ? `New ${resolved.ok ? resolved.manifest.name : driver} connection` : name;

    return (
        <ChromeDialog
            title={title}
            onClose={onBack}
            focusesField={name === undefined}
            fillsPhone
            footer={
                <>
                    {onDelete ? (
                        <Button colorPalette="red" variant="ghost" me="auto" onClick={() => (using.length > 0 ? setAskingDelete(true) : onDelete())}>
                            Delete
                        </Button>
                    ) : null}
                    <Button variant="outline" onClick={onBack}>
                        Back
                    </Button>
                    <Button colorPalette="blue" disabled={nameProblem !== undefined || problems.length > 0} onClick={() => onKeep(trimmedName, config)}>
                        {name === undefined ? 'Add' : 'Keep'}
                    </Button>
                </>
            }
        >
            {!resolved.ok ? (
                <ChromeError text={`This connection's driver is not available here: ${resolved.reason}`} />
            ) : (
                <Stack gap="4">
                    {name === undefined && resolved.manifest.discovery ? (
                        <FoundOnNetwork
                            shell={shell}
                            manifest={resolved.manifest}
                            onChoose={(system) => setTyped((current) => ({ ...current, ...system.config }))}
                            chosen={typed}
                        />
                    ) : null}
                    <Field.Root invalid={nameProblem !== undefined}>
                        <Field.Label>Name</Field.Label>
                        <Input aria-label="Name" value={typedName} onChange={(event) => setTypedName(event.target.value)} />
                        <Field.HelperText>Tiles name the connection they read from; renaming it renames it in them too.</Field.HelperText>
                        <Field.ErrorText>{nameProblem}</Field.ErrorText>
                    </Field.Root>
                    {fields.map((field) => (
                        <Field.Root key={field.name} required={field.isRequired}>
                            <Field.Label>
                                {field.label}
                                <Field.RequiredIndicator />
                            </Field.Label>
                            {field.choices ? (
                                <NativeSelect.Root>
                                    <NativeSelect.Field aria-label={field.label} value={typed[field.name] ?? ''} onChange={(event) => setTyped((current) => ({ ...current, [field.name]: event.target.value }))}>
                                        {field.isRequired && typed[field.name] ? null : <option value="">{field.isRequired ? 'Choose…' : 'Not set'}</option>}
                                        {field.choices.map((choice) => (
                                            <option key={choice.value} value={choice.value}>
                                                {choice.label}
                                            </option>
                                        ))}
                                    </NativeSelect.Field>
                                    <NativeSelect.Indicator />
                                </NativeSelect.Root>
                            ) : (
                                <Input
                                    aria-label={field.label}
                                    type={field.kind === 'secret' ? 'password' : 'text'}
                                    inputMode={field.kind === 'number' ? 'numeric' : undefined}
                                    autoComplete="off"
                                    placeholder={field.kind === 'secret' && stored[field.name] !== undefined ? 'Stored; type a new one to replace it' : undefined}
                                    value={typed[field.name] ?? ''}
                                    onChange={(event) => setTyped((current) => ({ ...current, [field.name]: event.target.value }))}
                                />
                            )}
                            {field.description ? <Field.HelperText>{field.description}</Field.HelperText> : null}
                        </Field.Root>
                    ))}
                    {problems.length > 0 ? <Text color="fg.muted" fontSize="sm">{problems.join('; ')}.</Text> : null}
                    <HStack gap="3">
                        <Button variant="outline" disabled={problems.length > 0} loading={tested?.status === 'testing'} onClick={() => void test()}>
                            Test the connection
                        </Button>
                    </HStack>
                    {tested?.status === 'done' ? (
                        tested.result.ok ? (
                            <ChromeNote status="success" text={masked(tested.result.detail, secrets)} />
                        ) : (
                            <ChromeError text={`It did not work: ${masked(tested.result.reason, secrets)}`} />
                        )
                    ) : null}
                    {tested?.status === 'failed' ? <ChromeError text={masked(tested.reason, secrets)} /> : null}
                </Stack>
            )}
        </ChromeDialog>
    );
}

/** `p1`, `p1-2`, … the driver's id or the next free name. */
function suggestedName(dashboard: Dashboard, driver: string) : string {
    const base = driver.replace(/[^A-Za-z0-9_-]/g, '-');
    let candidate = base;
    for (let n = 2; dashboard.connections[candidate] !== undefined; n++) {
        candidate = `${base}-${n}`;
    }

    return candidate;
}

/** The driver's systems on the network (M-8), offered first; a choice fills in the address. */
function FoundOnNetwork({ shell, manifest, chosen, onChoose }: { shell: Bridge; manifest: DriverManifest; chosen: Readonly<Record<string, string>>; onChoose: (system: FoundSystem) => void }) : ReactNode {
    const [found, setFound] = useState<FoundSystem[] | string>();
    const discovery = manifest.discovery;

    useEffect(() => {
        if (!discovery) {
            return undefined;
        }

        let isCurrent = true;
        discoverSystems(shell, discovery).then(
            (systems) => isCurrent && setFound(systems),
            (error: unknown) => isCurrent && setFound(messageOf(error)),
        );

        return () => {
            isCurrent = false;
        };
    }, [shell, discovery]);

    let rows: ReactNode;
    if (found === undefined) {
        rows = (
            <HStack gap="2" px="4" py="3" color="fg.muted">
                <Spinner size="sm" />
                <Text>Looking on this network…</Text>
            </HStack>
        );
    } else if (typeof found === 'string') {
        rows = <SettingsRow title={<Text as="span" color="fg.muted">This device cannot look on the network</Text>} detail={found} />;
    } else if (found.length === 0) {
        rows = <SettingsRow title={<Text as="span" color="fg.muted">None found</Text>} detail="Type its address below." />;
    } else {
        rows = found.map((system) => (
            <SettingsRow
                key={system.name}
                icon="wifi"
                title={system.name}
                detail={system.address}
                isCurrent={Object.entries(system.config).every(([key, value]) => chosen[key] === value)}
                opens={false}
                onClick={() => onChoose(system)}
            />
        ));
    }

    return <SettingsGroup title="On this network">{rows}</SettingsGroup>;
}
